import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Loader2, Eye, Users, Clock3, ShieldAlert,
  ShieldCheck, Info, FlaskConical, Gauge, Monitor, Smartphone,
  Tablet, Link2, FileText,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

const RANGES = [
  { days: "all", label: "All time" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last year" },
];

const METRICS = [
  { key: "page_views", label: "Page views", icon: Eye },
  { key: "sessions", label: "Sessions", icon: Clock3 },
  { key: "unique_visitors", label: "Unique visitors", icon: Users },
];export default function AdminAnalyticsV2() {
  const [days, setDays] = useState("all");
  const [mode, setMode] = useState("all");
  const [chartMetric, setChartMetric] = useState("page_views");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (windowDays, windowMode) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(
        `/admin/analytics/v2/summary?days=${windowDays}&mode=${windowMode}`,
      );
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days, mode); }, [days, mode]);

  const maxDaily = useMemo(() => {
    if (!data?.daily?.length) return 0;
    return Math.max(...data.daily.map((d) => d[chartMetric] || 0));
  }, [data, chartMetric]);

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-analytics-v2"
    >
      <Header />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <FlaskConical size={16} strokeWidth={2.5} />
              <span className="label-kicker">Site analytics · Beta</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Visitors, sessions and views — properly separated.
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              Three distinct metrics, a transparent bot-detection panel, and a
              toggle to see traffic with or without suspected automation.
            </p>
            <RouterLink
              to="/admin/analytics"
              data-testid="analytics-v2-classic-link"
              className="mt-2 inline-block text-xs font-bold uppercase tracking-wider text-brand-accent underline-offset-2 hover:underline"
            >
              ← Back to the classic dashboard
            </RouterLink>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            data-testid="analytics-v2-range"
            className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm font-bold"
          >
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>{r.label}</option>
            ))}
          </select>
        </div>

        <ModeToggle mode={mode} setMode={setMode} />

        {error ? (
          <p
            className="mt-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="analytics-v2-error"
          >
            {error}
          </p>
        ) : null}

        {loading && !data ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading analytics…
          </div>
        ) : data ? (
          <div className="mt-6 space-y-8" data-testid="analytics-v2-dashboard">
            <HistoricalLimitationBanner since={data.classification_available_since} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {METRICS.map((m) => (
                <StatCard
                  key={m.key}
                  label={m.label}
                  value={data[m.key]}
                  hint={data.definitions[m.key]}
                  icon={m.icon}
                  testid={`stat-v2-${m.key}`}
                  active={chartMetric === m.key}
                  onClick={() => setChartMetric(m.key)}
                />
              ))}
            </div>

            <AvgPageViewsCard value={data.page_views_per_visit} hint={data.definitions.page_views_per_visit} />

            <DailyChart
              daily={data.daily}
              metricKey={chartMetric}
              maxDaily={maxDaily}
              onPickMetric={setChartMetric}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <DeviceBreakdown devices={data.devices} mode={mode} />
              <TrafficSources referrers={data.top_referrers} mode={mode} />
              <TopPages pages={data.top_pages} mode={mode} />
            </div>

            <BotPanel panel={data.bot_panel} mode={mode} />
          </div>
        ) : null}
      </main>
    </div>
  );
}

function ModeToggle({ mode, setMode }) {
  return (
    <div
      className="inline-flex rounded-md border border-black/15 bg-[#F8FAFC] p-1"
      data-testid="analytics-v2-mode-toggle"
    >
      {[
        { key: "all", label: "All traffic" },
        { key: "human", label: "Likely human only" },
      ].map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => setMode(opt.key)}
          data-testid={`analytics-v2-mode-${opt.key}`}
          className={`rounded-sm px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wide transition-colors ${
            mode === opt.key
              ? "bg-[#0A0F17] text-white"
              : "text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function HistoricalLimitationBanner({ since }) {
  const sinceLabel = since
    ? new Date(since).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;
  return (
    <div
      className="flex items-start gap-3 rounded-md border border-dashed border-[#6CABDD]/50 bg-[#6CABDD]/5 px-4 py-3"
      data-testid="analytics-v2-historical-banner"
    >
      <Info size={16} className="mt-0.5 shrink-0 text-brand-accent" />
      <p className="text-xs leading-relaxed text-[var(--brand-body)]">
        {sinceLabel ? (
          <>
            Bot/automation signals (user-agent + network type) are only recorded for
            traffic since <strong>{sinceLabel}</strong>. Events from before that date
            are still counted in every total below, but can&apos;t be reliably split
            between &quot;All traffic&quot; and &quot;Likely human only&quot; — treat
            that boundary as a limitation of the historical data, not a gap in real
            traffic. Earlier data also predates this dashboard entirely: the old
            analytics page silently dropped obviously self-announcing bots (e.g.
            Googlebot) the moment they visited, so historical totals already exclude
            some automated hits that today&apos;s &quot;All traffic&quot; view keeps.
          </>
        ) : (
          <>
            Bot/automation classification hasn&apos;t started recording yet in this
            window — once new visits come in, this banner will show exactly which
            date the signal became available from.
          </>
        )}
      </p>
    </div>
  );
}

function StatCard({ label, value, hint, icon: Icon, testid, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-md border p-5 text-left transition-colors ${
        active ? "border-[#0A0F17] bg-[#F8FAFC]" : "border-black/10 bg-white hover:border-black/25"
      }`}
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Icon size={14} strokeWidth={2.5} />
        <span className="label-kicker">{label}</span>
      </div>
      <p className="mt-3 font-display text-4xl font-black tracking-tight">
        {(value ?? 0).toLocaleString("en-GB")}
      </p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p>
    </button>
  );
}

function DailyChart({ daily, metricKey, maxDaily, onPickMetric }) {
  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  return (
    <section className="rounded-md border border-black/10 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-black">Daily {metric.label.toLowerCase()}</h2>
        <div className="flex flex-wrap gap-1.5" data-testid="analytics-v2-chart-metric-picker">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => onPickMetric(m.key)}
              data-testid={`chart-metric-${m.key}`}
              className={`rounded-sm px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                m.key === metricKey
                  ? "bg-[#0A0F17] text-white"
                  : "border border-black/15 text-[var(--brand-body)] hover:border-black/40"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {!daily?.length ? (
        <p className="mt-4 text-sm text-[var(--brand-body)]">No traffic in this window yet.</p>
      ) : (
        <div className="mt-5 flex items-end gap-1 overflow-x-auto pb-2" data-testid="analytics-v2-daily-chart">
          {daily.map((d) => {
            const v = d[metricKey] || 0;
            const h = maxDaily ? Math.max(4, Math.round((v / maxDaily) * 160)) : 4;
            return (
              <div
                key={d.day}
                className="flex shrink-0 flex-col items-center gap-1"
                style={{ width: 30 }}
                data-testid={`analytics-v2-day-${d.day}`}
                title={`${d.day}: ${d.page_views} views · ${d.sessions} sessions · ${d.unique_visitors} visitors`}
              >
                <span className="text-[10px] font-mono text-[var(--brand-body)]">{v}</span>
                <div className="w-3 rounded-t-sm bg-[#6CABDD]" style={{ height: h }} />
                <span className="rotate-45 origin-top-left text-[10px] text-[var(--brand-body)] whitespace-nowrap" style={{ height: 20 }}>
                  {d.day.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function BotPanel({ panel, mode }) {
  if (!panel) return null;
  const excludedRows = [
    {
      key: "declared_bot", label: "Self-declared crawlers / bots",
      explain: "User-agent literally identifies itself as a bot, crawler, monitoring tool or bare HTTP client (e.g. Googlebot, curl). High confidence on its own — a declaration, not a guess. Excluded from \"Likely human\".",
    },
    {
      key: "headless_automation", label: "Headless / automation-framework browsers",
      explain: "User-agent carries a HeadlessChrome / Puppeteer / Playwright / Selenium signature. No real human browser sends these — high confidence on its own. Excluded from \"Likely human\".",
    },
    {
      key: "datacenter_and_rapid_fire", label: "Datacenter network + this exact event is part of a rapid-fire cluster",
      explain: "This specific page-view is on a cloud/hosting network AND is itself one of 3+ consecutive views under 1.5s apart. Excluded only because TWO independent signals agree on THIS event — neither counts alone.",
    },
  ];
  const informationalRows = [
    {
      key: "datacenter_only", label: "Datacenter network only (not excluded)",
      explain: "Request came from a cloud/hosting ASN, but with no other suspicious signal — e.g. VPN users, corporate networks, or genuinely misclassified ISPs. Shown for transparency only — never excluded by itself.",
    },
    {
      key: "rapid_fire_only", label: "Rapid-fire cluster member only (not excluded)",
      explain: "This event is one of 3+ consecutive page-views under 1.5s apart in the same visit (e.g. a double-tap or two tabs opened at once), but on an ordinary residential/ISP network. Only the events IN that cluster are counted here — the visitor's other, normally-paced page views are never swept in. Shown for transparency only — never excluded by itself.",
    },
  ];
  const flaggedPct = panel.total_events
    ? Math.round((panel.likely_automated / panel.total_events) * 100)
    : 0;
  return (
    <section
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid="analytics-v2-bot-panel"
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <ShieldAlert size={14} strokeWidth={2.5} />
        <span className="label-kicker">Transparent bot detection</span>
      </div>
      <h2 className="mt-1 font-display text-lg font-black">
        {panel.likely_automated.toLocaleString("en-GB")} of {panel.total_events.toLocaleString("en-GB")} events
        ({flaggedPct}%) excluded as likely automated
      </h2>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        This breakdown always shows ALL traffic for the window, no matter which
        toggle is selected above — nothing is ever hidden from this panel.
        {mode === "human" ? " The stat cards above are currently excluding the rows below marked \"Excluded\"." : " The stat cards above currently include every row below."}
      </p>

      <h3 className="mt-5 label-kicker text-[#E63946]">Excluded from &quot;Likely human&quot;</h3>
      <BotPanelRows rows={excludedRows} panel={panel} />

      <h3 className="mt-5 label-kicker text-brand-accent">Informational only — never excluded on their own</h3>
      <BotPanelRows rows={informationalRows} panel={panel} />
      <p className="mt-2 text-[11px] text-[var(--brand-body)]">
        These two rows cover {panel.informational_only_total.toLocaleString("en-GB")} events in
        total — shown so you can see exactly what the detector noticed, without it
        ever affecting your counts.
      </p>

      <h3 className="mt-5 label-kicker text-[var(--brand-body)]">Predates bot classification</h3>
      <BotPanelRows
        rows={[{
          key: "no_classification_data", label: "Historical — no signal ever captured",
          explain: "Recorded before this detection system existed — no user-agent/network signal was ever captured for these, so they can't be classified either way.",
        }]}
        panel={panel}
      />

      <p className="mt-4 flex items-start gap-2 text-[11px] leading-snug text-[var(--brand-body)]">
        <ShieldCheck size={13} className="mt-0.5 shrink-0 text-brand-accent" />
        Nothing is ever discarded because of a suspected-bot signal — every event
        above is still stored and counted in &quot;All traffic&quot;. Country,
        page-view count and &quot;no referrer&quot; are never used as a flagging
        signal on their own, and a rapid-fire cluster only ever covers the exact
        events inside it — never a visitor&apos;s unrelated, normally-paced browsing.
      </p>
    </section>
  );
}

function BotPanelRows({ rows, panel }) {
  return (
    <ul className="mt-2 space-y-2" data-testid="analytics-v2-bot-panel-rows">
      {rows.map((row) => (
        <li
          key={row.key}
          className="rounded-md border border-black/5 bg-[#F8FAFC] px-3 py-2.5"
          data-testid={`bot-panel-row-${row.key}`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-display text-sm font-bold">{row.label}</span>
            <span className="shrink-0 font-mono text-sm">
              {(panel[row.key] || 0).toLocaleString("en-GB")}
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-snug text-[var(--brand-body)]">{row.explain}</p>
        </li>
      ))}
    </ul>
  );
}

function AvgPageViewsCard({ value, hint }) {
  return (
    <div className="rounded-md border border-black/10 bg-white p-5" data-testid="stat-v2-page_views_per_visit">
      <div className="flex items-center gap-2 text-brand-accent">
        <Gauge size={14} strokeWidth={2.5} />
        <span className="label-kicker">Avg page views per visit</span>
      </div>
      <p className="mt-3 font-display text-4xl font-black tracking-tight">
        {(value ?? 0).toLocaleString("en-GB")}
      </p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p>
    </div>
  );
}

const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

function DeviceBreakdown({ devices, mode }) {
  const rows = devices || [];
  const total = rows.reduce((s, r) => s + (r.page_views || 0), 0) || 1;
  return (
    <section className="rounded-md border border-black/10 bg-white p-5" data-testid="analytics-v2-devices">
      <h2 className="font-display text-base font-black">Devices</h2>
      <p className="mt-1 text-[11px] text-[var(--brand-body)]">
        {mode === "human" ? "Likely-human page views only." : "All traffic, including suspected automation."}
      </p>
      <ul className="mt-3 space-y-2">
        {rows.length ? rows.map((d) => {
          const Icon = DEVICE_ICONS[d.device] || Monitor;
          const pct = Math.round((100 * (d.page_views || 0)) / total);
          return (
            <li key={d.device} className="flex items-center justify-between gap-2 text-sm" data-testid={`analytics-v2-device-${d.device}`}>
              <span className="flex items-center gap-2 capitalize"><Icon size={14} className="text-brand-accent" />{d.device}</span>
              <span className="font-mono">{(d.page_views || 0).toLocaleString("en-GB")} <span className="text-[var(--brand-body)]">({pct}%)</span></span>
            </li>
          );
        }) : <p className="text-sm text-[var(--brand-body)]">No traffic in this window yet.</p>}
      </ul>
    </section>
  );
}

function TrafficSources({ referrers, mode }) {
  const rows = (referrers || []).slice(0, 8);
  return (
    <section className="rounded-md border border-black/10 bg-white p-5" data-testid="analytics-v2-referrers">
      <h2 className="font-display text-base font-black">Traffic sources</h2>
      <p className="mt-1 text-[11px] text-[var(--brand-body)]">
        {mode === "human" ? "Likely-human page views only." : "All traffic, including suspected automation."}
      </p>
      <ul className="mt-3 space-y-2">
        {rows.length ? rows.map((r) => (
          <li key={r.referrer} className="flex items-center justify-between gap-2 text-sm" data-testid={`analytics-v2-referrer-row`}>
            <span className="flex min-w-0 items-center gap-2 truncate"><Link2 size={14} className="shrink-0 text-brand-accent" /><span className="truncate">{r.referrer}</span></span>
            <span className="shrink-0 font-mono">{(r.page_views || 0).toLocaleString("en-GB")}</span>
          </li>
        )) : <p className="text-sm text-[var(--brand-body)]">No traffic in this window yet.</p>}
      </ul>
    </section>
  );
}

function TopPages({ pages, mode }) {
  const rows = (pages || []).slice(0, 8);
  return (
    <section className="rounded-md border border-black/10 bg-white p-5" data-testid="analytics-v2-top-pages">
      <h2 className="font-display text-base font-black">Popular pages</h2>
      <p className="mt-1 text-[11px] text-[var(--brand-body)]">
        {mode === "human" ? "Likely-human page views only." : "All traffic, including suspected automation."}
      </p>
      <ul className="mt-3 space-y-2">
        {rows.length ? rows.map((p) => (
          <li key={p.path} className="flex items-center justify-between gap-2 text-sm" data-testid="analytics-v2-page-row">
            <span className="flex min-w-0 items-center gap-2 truncate"><FileText size={14} className="shrink-0 text-brand-accent" /><span className="truncate">{p.path}</span></span>
            <span className="shrink-0 font-mono">{(p.page_views || 0).toLocaleString("en-GB")}</span>
          </li>
        )) : <p className="text-sm text-[var(--brand-body)]">No traffic in this window yet.</p>}
      </ul>
    </section>
  );
}
